/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module    public/js/modals/smsMessageModal.js
 * @function  openSmsMessageModal
 * @layer     ui-modal
 * @summary   Opens a modal for composing SMS messages using playbook templates.
 *
 * @description
 * • Renders a Mustache template to prefill the SMS body when available.
 * • Lets the seller swap between templates (when multiple) and edit the text.
 * • Returns the selected template + final message so the caller can build the
 *   deep link before opening the native SMS app.
 * • Supports a copy-first flow for providers (e.g. Zoom) that cannot prefill
 *   the SMS body via deeplink.
 *
 * ## Public API
 * ```ts
 * openSmsMessageModal({
 *   templateId?: string,
 *   templateIds?: string[],
 *   templateData?: Record<string, unknown>,
 *   mergeVars?: Record<string, unknown>,
 *   contactName?: string,
 *   initialMessage?: string,
 *   helperMessage?: string,
 *   copyFirst?: boolean,
 *   copyButtonLabel?: string,
 *   primaryButtonLabel?: string,
 * }): Promise<{
 *   templateId: string | null;
 *   message: string;
 *   copied?: boolean;
 *   copiedAt?: number | null;
 *   copyMethod?: string | null;
 * } | null>
 * ```
 *
 * @author    TopSail AI
 * @version   1.0.0
 * @since     2025-10-02
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { TemplateUpdateFlow } from './templateUpdateFlow.js';
import { applyFocusModeTooltips } from '../focusmode/tooltipService.js';
import { htmlToPlainText } from '../utils/htmlToPlainText.js';

const HOST_ID = 'smsMessageModal';

/**
 * Attempt to copy text to the system clipboard, falling back to a hidden
 * `<textarea>` when the modern API is unavailable.
 *
 * @param {string} value - Message body to copy.
 * @returns {Promise<{ success: boolean, method: string | null }>} Result flag
 *          plus the strategy that succeeded (`"clipboard"` or `"execCommand"`).
 */
async function copyToClipboard(value = '') {
  if (!value) {
    return { success: false, method: 'empty' };
  }

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return { success: true, method: 'clipboard' };
    }
  } catch (err) {
    console.warn('[SmsMessageModal] clipboard.writeText failed', err);
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return { success: ok, method: ok ? 'execCommand' : 'execCommand_failed' };
  } catch (err) {
    console.warn('[SmsMessageModal] execCommand copy failed', err);
    return { success: false, method: 'fallback_failed' };
  }
}

/**
 * Escape HTML entities so template names/contact names render safely in the DOM.
 *
 * @param {string} value - Raw string to escape.
 * @returns {string} HTML-safe string.
 */
function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export async function openSmsMessageModal(options = {}) {
  const {
    templateId = '',
    templateIds = [],
    templateData = {},
    mergeVars = {},
    contactName = '',
    initialMessage = '',
    helperMessage = '',
    copyFirst = false,
    copyButtonLabel = 'Copy Text',
    primaryButtonLabel = 'Next',
    enableTooltips = typeof document !== 'undefined' &&
      document.body?.classList.contains('focusmode') &&
      document.body?.getAttribute('data-focus-tooltips') !== 'false',
  } = options;

  const di = (await import('../bootstrap/app.js')).default;
  const modalService = di.modalService || di.modal;
  const errorUI = di.errorUI;

  const uniqueTemplates = Array.isArray(templateIds)
    ? Array.from(new Set(templateIds.filter(Boolean)))
    : [];
  if (templateId && !uniqueTemplates.includes(templateId)) {
    uniqueTemplates.unshift(templateId);
  }
  let currentTemplateId = templateId || uniqueTemplates[0] || '';
  let messageDraft = initialMessage || '';
  let viewDraft = '';
  let viewHtml = '';
  let rawTemplateHtml = '';
  let templateEditMode = false;
  let updateEnabled = false;
  const copyState = { copied: false, copiedAt: null, method: null };

  const templateUpdateFlow = new TemplateUpdateFlow({
    di,
    modalService,
    templateId,
    templateIds: uniqueTemplates,
    templateData,
    mergeVars,
  });

  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    host.className = 'modal-overlay hidden';
    document.body.appendChild(host);
    modalService.register(HOST_ID, host);
  }

  const escapedName = contactName ? escapeHtml(contactName) : '';
  const headerSuffix = escapedName ? ` for ${escapedName}` : '';
  const showCopyUI = copyFirst === true;
  const canUpdateTemplate = uniqueTemplates.length > 0;

  const templateSelectMarkup =
    uniqueTemplates.length > 1
      ? `<div class="flex items-center gap-2 mb-4">
           <label class="text-sm font-medium shrink-0" for="sms-message-template">Template</label>
           <select id="sms-message-template" class="input flex-1 h-8 px-2 text-sm">
             ${uniqueTemplates
               .map(
                 (id) =>
                   `<option value="${escapeHtml(id)}" ${
                     id === currentTemplateId ? 'selected' : ''
                   }>${escapeHtml(id)}</option>`
               )
               .join('')}
           </select>
         </div>`
      : '';

  const helperText =
    helperMessage ||
    (showCopyUI
      ? 'Review the message below, tweak if needed, then copy it before sending.'
      : uniqueTemplates.length
        ? 'Select a template to preview the text. Edit before sending if needed.'
        : 'No template is associated with this step. Draft your message below.');

  host.innerHTML = `
    <div class="modal-container modal-teal sms-message-modal">
      <div class="focus-modal__header">
        <div class="focus-modal__title-group">
          <h3>Text Message${headerSuffix}</h3>
        </div>
        ${
          canUpdateTemplate
            ? `<div class="focus-modal__header-actions">
                 <button type="button" id="sms-edit-template" class="btn-pill btn--grey btn--info-sm" data-focus-tooltip="Edit the selected SMS template.">
                   Edit Template
                 </button>
                 <button type="button" id="sms-update-template" class="btn-pill btn--grey btn--info-sm hidden" data-focus-tooltip="Save changes to the selected SMS template.">
                   Update Template
                 </button>
               </div>`
            : ''
        }
      </div>
      <p
        id="sms-template-mode"
        class="focus-modal__mode-banner text-xs text-gray-500 mb-3 hidden"
      >
        Template edit mode: "Next" and "Copy" are disabled.
      </p>
      <p
        id="sms-template-disabled"
        class="focus-modal__mode-banner text-xs text-gray-500 mb-3 hidden"
      >
        Template update unavailable. Check tenant access or template permissions.
      </p>
      ${templateSelectMarkup}
      <p class="text-xs text-gray-500 mb-3" data-helper>${escapeHtml(
        helperText
      )}</p>
      <label for="sms-message-editor" class="block text-sm font-medium mb-1">Message</label>
      <div id="sms-message-editor" class="quill-editor bg-white hidden"></div>
      <textarea id="sms-message-text"
                class="input w-full h-48"
                placeholder="Draft your text message here.">${escapeHtml(
                  messageDraft
                )}</textarea>
      <p class="text-xs text-gray-500 mt-2">
        You will paste this message into your SMS app after continuing.
      </p>
      ${
        showCopyUI
          ? '<p class="text-xs text-gray-500 mt-2" data-copy-status style="min-height:1.25rem;"></p>'
          : ''
      }
      <footer class="focus-modal__footer mt-4">
        <div
          class="focus-modal__footer-actions"
          data-actions="${showCopyUI ? '3' : '2'}"
        >
          <button type="button" class="btn-pill btn--grey btn--info-sm btn--secondary-action" data-action="cancel" data-focus-tooltip="Close without saving this text message.">Cancel</button>
          ${
            showCopyUI
              ? `<button type="button" class="btn-pill btn--grey btn--info-sm focus-modal__alternate-action" data-action="copy" data-focus-tooltip="Copy this text message to your clipboard.">${escapeHtml(
                  copyButtonLabel
                )}</button>`
              : ''
          }
          <button type="button" class="btn-pill btn--orange btn--info-sm btn--primary-action" data-action="next" data-focus-tooltip="Continue to your SMS app with this message.">${escapeHtml(
            primaryButtonLabel
          )}</button>
        </div>
      </footer>
    </div>
  `;

  applyFocusModeTooltips(host, { enableTooltips });

  const textarea = host.querySelector('#sms-message-text');
  const editor = host.querySelector('#sms-message-editor');
  const templateSelect = host.querySelector('#sms-message-template');
  const helperEl = host.querySelector('[data-helper]');
  const copyBtn = host.querySelector('[data-action="copy"]');
  const statusEl = host.querySelector('[data-copy-status]');
  const nextBtn = host.querySelector('[data-action="next"]');
  const cancelBtn = host.querySelector('[data-action="cancel"]');
  const editBtn = host.querySelector('#sms-edit-template');
  const updateBtn = host.querySelector('#sms-update-template');
  const templateModeBanner = host.querySelector('#sms-template-mode');
  const templateDisabledBanner = host.querySelector('#sms-template-disabled');
  const detach = [];
  let quill = null;
  let suppressQuillSync = false;

  if (editor) {
    if (!window.Quill) {
      await import(
        'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.min.js'
      );
      if (!document.getElementById('quill-css')) {
        const link = document.createElement('link');
        link.id = 'quill-css';
        link.rel = 'stylesheet';
        link.href =
          'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.snow.css';
        document.head.appendChild(link);
      }
    }

    const toolbarOptions = [
      [{ size: [] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
    ];

    quill = new Quill(editor, {
      theme: 'snow',
      modules: {
        toolbar: toolbarOptions,
      },
    });
    quill.root.setAttribute('aria-label', 'SMS template editor');
    quill.on('text-change', () => {
      if (suppressQuillSync) {
        return;
      }
      if (templateEditMode) {
        rawTemplateHtml = quill.root.innerHTML;
        messageDraft = rawTemplateHtml;
        if (textarea) {
          textarea.value = rawTemplateHtml;
        }
        return;
      }
      viewHtml = quill.root.innerHTML;
      viewDraft = htmlToPlainText(viewHtml);
      messageDraft = viewDraft;
      if (textarea) {
        textarea.value = viewDraft;
      }
    });
  }

  /**
   * Update Quill HTML while preventing sync handlers from running.
   *
   * @param {string} html - HTML content to render inside Quill.
   * @returns {void}
   */
  const setQuillHtml = (html) => {
    if (!quill) return;
    suppressQuillSync = true;
    quill.root.innerHTML = html || '';
    suppressQuillSync = false;
  };

  if (editor) {
    editor.classList.remove('hidden');
  }
  if (textarea) {
    textarea.classList.add('hidden');
  }

  /**
   * Render the selected template using TemplateService and populate the textarea.
   *
   * @param {string} id - Template identifier chosen by the seller.
   * @returns {Promise<void>} Resolves once the textarea contains rendered copy.
   */
  const loadTemplate = async (id) => {
    currentTemplateId = id || '';

    if (!currentTemplateId) {
      messageDraft = initialMessage || '';
      viewDraft = messageDraft;
      viewHtml = messageDraft;
      rawTemplateHtml = '';
      updateEnabled = false;
      if (textarea) textarea.value = messageDraft;
      if (quill) {
        setQuillHtml(viewHtml);
        quill.enable(true);
      }
      if (helperEl)
        helperEl.textContent = showCopyUI
          ? 'No template selected. Draft your message below, then copy it.'
          : 'No template selected. Draft your text message below.';
      if (templateDisabledBanner) {
        templateDisabledBanner.classList.toggle('hidden', true);
      }
      if (editBtn) {
        editBtn.disabled = true;
      }
      if (updateBtn) {
        updateBtn.disabled = true;
      }
      return;
    }

    try {
      const updateState =
        (await templateUpdateFlow.loadRawTemplate(currentTemplateId)) || {};
      updateEnabled = !!updateState.updateEnabled;
      rawTemplateHtml =
        templateUpdateFlow.getRawTemplateHtml() || rawTemplateHtml;
      if (templateDisabledBanner) {
        templateDisabledBanner.classList.toggle('hidden', updateEnabled);
      }
      if (editBtn) {
        editBtn.disabled = !updateEnabled;
      }
      if (updateBtn) {
        updateBtn.disabled = !updateEnabled;
      }
      const ctx = { ...(mergeVars || {}), ...(templateData || {}) };
      const rendered = await di.templateService.render(currentTemplateId, ctx);
      viewHtml = rendered.html || '';
      viewDraft = htmlToPlainText(rendered.html || '');
      if (!templateEditMode) {
        messageDraft = viewDraft;
        if (textarea) {
          textarea.value = messageDraft;
        }
        if (quill) {
          setQuillHtml(viewHtml);
          quill.enable(true);
        }
      }
      if (helperEl)
        helperEl.textContent = showCopyUI
          ? 'Review the message below, tweak if needed, then copy it.'
          : 'Review the message below, tweak if needed, then continue.';
    } catch (err) {
      console.error('[SmsMessageModal] render failed', err);
      messageDraft = initialMessage || '';
      viewDraft = messageDraft;
      viewHtml = '';
      updateEnabled = false;
      if (textarea) textarea.value = messageDraft;
      if (quill) {
        setQuillHtml('');
        quill.enable(true);
      }
      if (helperEl)
        helperEl.textContent = showCopyUI
          ? 'Unable to load the selected template. Draft your message below, then copy it.'
          : 'Unable to load the selected template. Draft your message below.';
      if (templateDisabledBanner) {
        templateDisabledBanner.classList.toggle('hidden', true);
      }
      if (editBtn) {
        editBtn.disabled = true;
      }
      if (updateBtn) {
        updateBtn.disabled = true;
      }
      errorUI?.global?.({
        text: 'Unable to render SMS message template.',
        type: 'error',
        duration: 4000,
      });
    }
  };

  if (textarea) {
    /**
     * Keep the in-memory draft synchronised with user edits.
     *
     * @param {Event} ev - Input event dispatched by the textarea.
     * @returns {void}
     */
    function onInput(ev) {
      messageDraft = ev.target.value;
      if (templateEditMode) {
        rawTemplateHtml = messageDraft;
      } else {
        viewDraft = messageDraft;
      }
    }
    textarea.addEventListener('input', onInput);
    detach.push(() => textarea.removeEventListener('input', onInput));
  }

  if (templateSelect) {
    /**
     * Handle template switching by re-rendering the selected option.
     *
     * @param {Event} ev - Change event fired by the `<select>` element.
     * @returns {void}
     */
    const onChange = async (ev) => {
      await loadTemplate(ev.target.value);
      await setTemplateEditMode(false);
    };
    templateSelect.addEventListener('change', onChange);
    detach.push(() => templateSelect.removeEventListener('change', onChange));
  }

  if (copyBtn) {
    /**
     * Copy the current draft to clipboard and surface inline feedback.
     *
     * @returns {Promise<void>} Resolves after attempting the copy action.
     */
    const onCopy = async () => {
      const html =
        quill?.root?.innerHTML ?? textarea?.value ?? messageDraft ?? '';
      messageDraft = htmlToPlainText(html);
      const { success, method } = await copyToClipboard(messageDraft);
      copyState.copied = success;
      copyState.copiedAt = success ? Date.now() : null;
      copyState.method = method;
      if (statusEl) {
        statusEl.textContent = success
          ? 'Copied message to clipboard.'
          : 'Unable to copy. Select the text manually to copy it.';
      }
      if (!success) {
        errorUI?.global?.({
          text: 'Copy failed. Try selecting the text manually.',
          type: 'warning',
          duration: 4000,
        });
      }
    };
    copyBtn.addEventListener('click', onCopy);
    detach.push(() => copyBtn.removeEventListener('click', onCopy));
  }

  /**
   * Toggle template edit mode for updating the stored template source.
   *
   * @param {boolean} nextMode - When true, switch into template edit mode.
   * @returns {Promise<void>} Resolves once the UI reflects the requested mode.
   */
  const setTemplateEditMode = async (nextMode) => {
    templateEditMode = nextMode;
    if (editBtn) {
      editBtn.textContent = templateEditMode
        ? 'Cancel Update'
        : 'Edit Template';
    }
    if (updateBtn) {
      updateBtn.classList.toggle('hidden', !templateEditMode);
    }
    if (templateModeBanner) {
      templateModeBanner.classList.toggle('hidden', !templateEditMode);
    }
    if (nextBtn) {
      nextBtn.disabled = templateEditMode;
    }
    if (copyBtn) {
      copyBtn.disabled = templateEditMode;
    }
    if (templateEditMode) {
      rawTemplateHtml =
        templateUpdateFlow.getRawTemplateHtml() || rawTemplateHtml;
      if (textarea) {
        textarea.value = rawTemplateHtml;
        textarea.classList.add('hidden');
      }
      if (quill) {
        setQuillHtml(rawTemplateHtml);
        quill.enable(true);
      }
      messageDraft = rawTemplateHtml;
      if (helperEl) {
        helperEl.textContent =
          'Editing template source. Update to save changes or cancel to discard.';
      }
      return;
    }

    rawTemplateHtml =
      quill?.root?.innerHTML ?? textarea?.value ?? rawTemplateHtml;
    messageDraft = viewDraft;
    if (textarea) {
      textarea.value = viewDraft;
      textarea.classList.add('hidden');
    }
    if (quill) {
      setQuillHtml(viewHtml);
      quill.enable(true);
    }
    if (helperEl) {
      helperEl.textContent = showCopyUI
        ? 'Review the message below, tweak if needed, then copy it.'
        : 'Review the message below, tweak if needed, then continue.';
    }
  };

  /**
   * Handle toggling the template edit mode.
   * @returns {Promise<void>}
   */
  const onEditTemplate = async () => {
    if (!updateEnabled) {
      errorUI?.global?.({
        text: 'Template updates are unavailable for this step.',
        type: 'warning',
        duration: 3000,
      });
      return;
    }
    await setTemplateEditMode(!templateEditMode);
  };

  /**
   * Persist the edited template source and refresh the rendered view.
   * @returns {Promise<void>}
   */
  const onUpdateTemplate = async () => {
    if (!currentTemplateId) return;
    if (!updateEnabled) {
      errorUI?.global?.({
        text: 'Template updates are unavailable for this step.',
        type: 'warning',
        duration: 3000,
      });
      return;
    }
    if (!templateEditMode) {
      errorUI?.global?.({
        text: 'Switch to Edit Template to update the saved template.',
        type: 'info',
        duration: 3000,
      });
      return;
    }
    if (updateBtn) {
      updateBtn.disabled = true;
      updateBtn.textContent = 'Updating…';
    }
    const rawHtml =
      quill?.root?.innerHTML ?? textarea?.value ?? rawTemplateHtml;
    try {
      const result = await templateUpdateFlow.confirmAndUpdate({
        html: rawHtml,
        templateId: currentTemplateId,
      });
      if (host.classList.contains('hidden')) {
        modalService.open(HOST_ID);
      }
      if (!result || result.updated !== true) {
        if (result && result.reason === 'missing-tenant-id') {
          errorUI?.global?.({
            text: 'Cannot update template because tenant information is missing.',
            type: 'error',
            duration: 4000,
          });
        }
        return;
      }
      const ctx = { ...(mergeVars || {}), ...(templateData || {}) };
      if (di?.templateService?.renderFromSource) {
        const rendered = await di.templateService.renderFromSource(
          { html: rawHtml },
          ctx
        );
        viewHtml = rendered.html || '';
        viewDraft = htmlToPlainText(rendered.html || '');
        messageDraft = viewDraft;
        if (textarea) {
          textarea.value = viewDraft;
        }
        if (quill) {
          setQuillHtml(viewHtml);
        }
      } else {
        await loadTemplate(currentTemplateId);
      }
      await setTemplateEditMode(false);
      quill?.root?.focus?.();
      errorUI?.global?.({
        text: 'Template updated.',
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      console.error('[SmsMessageModal] template update failed:', err);
      errorUI?.global?.({
        text: 'Failed to update template. Check permissions and try again.',
        type: 'error',
        duration: 4000,
      });
    } finally {
      if (updateBtn) {
        updateBtn.disabled = false;
        updateBtn.textContent = 'Update Template';
      }
    }
  };

  /**
   * Close the modal and run all registered cleanup callbacks.
   * @returns {void}
   */
  const closeModal = () => {
    try {
      modalService.close(HOST_ID);
    } catch (err) {
      console.warn('[SmsMessageModal] close failed', err);
    }
    detach.forEach((fn) => {
      try {
        fn();
      } catch (e) {
        console.warn('[SmsMessageModal] listener cleanup failed', e);
      }
    });
  };

  return new Promise((resolve) => {
    /**
     * Resolve the pending promise while ensuring the modal is closed.
     * @param {object|null} value - Data returned to the caller.
     * @returns {void}
     */
    const settle = (value) => {
      closeModal();
      resolve(value);
    };

    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => settle(null), { once: true });
    }

    if (nextBtn) {
      nextBtn.addEventListener(
        'click',
        () =>
          settle({
            templateId: currentTemplateId || null,
            message: htmlToPlainText(
              quill?.root?.innerHTML ?? textarea?.value ?? messageDraft ?? ''
            ),
            copied: copyState.copied,
            copiedAt: copyState.copiedAt,
            copyMethod: copyState.method,
          }),
        { once: true }
      );
    }

    if (editBtn) {
      editBtn.addEventListener('click', onEditTemplate);
      detach.push(() => editBtn.removeEventListener('click', onEditTemplate));
    }

    if (updateBtn) {
      updateBtn.addEventListener('click', onUpdateTemplate);
      detach.push(() =>
        updateBtn.removeEventListener('click', onUpdateTemplate)
      );
    }

    try {
      modalService.open(HOST_ID);
    } catch (err) {
      console.warn('[SmsMessageModal] open failed', err);
    }

    loadTemplate(currentTemplateId);
    quill?.root?.focus?.();
  });
}
